import type { CategoryInput, Categories, CategoryUpdateVariables, QuerySuggestion, QuerySuggestions, ProductsResponse, CategoryMapping, CategoryMappingInput, SmartSwapProduct } from "@/types";
import { graphqlClient } from "./graphqlClient";
import type { QuerySuggestionInput } from "@/types";

export async function addCategory(categoryInput: CategoryInput) {
  const query = `
    mutation AddCategory($categoryInput: CategoryInput!) {
      addCategory(categoryInput: $categoryInput) {
        id
      }
    }
  `;
  const variables = {
    categoryInput,
  };
  try {
    const resp = await graphqlClient(query, variables);
    return resp.data.categories;
  } catch (err) {
    console.log('[ERROR] ----------- addCategory --');
    console.log(err);
    throw err;
  }
}

export async function deleteCategory(uid: number) {
  const query = `
    mutation DeleteCategory($uid: Int!) {
      deleteCategory(uid: $uid)
    }
  `;
  const variables = {
    uid,
  };
  try {
    const resp = await graphqlClient(query, variables);
    return resp.data.categories;
  } catch (err) {
    console.log('[ERROR] ----------- deleteCategory --');
    console.log(err);
    throw err;
  }
}

export async function updateCategory(categoryUpdateVariables: CategoryUpdateVariables) {
  const query = `
    mutation UpdateCategory($uid: Int!, $categoryInput: CategoryInput!) {
      updateCategory(uid: $uid, categoryInput: $categoryInput) {
        id
      }
    }
  `;
  try {
    const resp = await graphqlClient(query, categoryUpdateVariables);
    return resp.data.categories;
  } catch (err) {
    console.log('[ERROR] ----------- updateCategory --');
    console.log(err);
    throw err;
  }
}

export async function updateProduct(productUpdateInput: any) {
  try {
    const mutation = `
      mutation UpdateProduct($productUpdateInput: ProductUpdateInput!) {
        updateProduct(productUpdateInput: $productUpdateInput)
      }
    `;
    const variables = {
      productUpdateInput,
    }
    const resp = await graphqlClient(mutation, variables);
    return resp.data;
  } catch (err) {
    console.log('[ERROR] ----------- updateProducts--');
    console.log(err.message);
    throw err;
  }
}

export async function deleteProduct(productId: string) {
  try {
    const mutation = `
      mutation DeleteProduct($productId: String!) {
        deleteProduct(productId: $productId)
      }
    `;
    const variables = {
      productId,
    }
    const resp = await graphqlClient(mutation, variables);
    return resp.data;
  } catch (err) {
    console.log('[ERROR] ----------- deleteProducts--');
    console.log(err.message);
    throw err;
  }
}

export function getProducts(categoryId: string, subcategory: string | null | undefined, pageNo?: number) {
  return async function(): Promise<ProductsResponse> {
    const query = `
      query GetProducts($categoryLevel0: String!, $categoryLevel1: String, $pageNo: Int!) {
        products(categoryLevel0: $categoryLevel0, categoryLevel1: $categoryLevel1, pageNo: $pageNo) {
          totalCount
          pageNo
          pages {
            page
            isSelected
            label
          }
          products {
            productId
            name
            brand
            barcode
            description
            packageSize
            storeId
            categories {
              level0
              level1
            }
            categoryHierarchy {
              level0
              level1
            }
            imageUrls
            price {
              amountAsCents
              saveAmountAsCents
              wasAmountAsCents
              tags
              comparativePricing {
                unitOfMeasure
                quantity
                priceAsCents
              }
            }
            sellableQuantities {
              increment
              maxQty
              minQty
              sellableUom
            }
          }
        }
      }
    `;
    const variables = {
      categoryLevel0: categoryId,
      categoryLevel1: subcategory,
      pageNo,
    };

    try {
      const resp = await graphqlClient(query, variables);
      return resp.data.products;
    } catch (err) {
      console.log('[ERROR] ----------- getProducts--');
      console.log(err.message);
      throw err;
    }
  }
}

export function getProduct(productId: string) {
  return async function (): Promise<Product> {
    const query = `
      query GetProduct($productId: String!) {
        product(productId: $productId) {
          productId
          name
          brand
          barcode
          description
          packageSize
          storeId
          imageUrls
          ingredients
          allergens
          countryOfOrigin
          price {
            amountAsCents
            saveAmountAsCents
            wasAmountAsCents
            tags
            comparativePricing {
              unitOfMeasure
              quantity
              priceAsCents
            }
          }
          sellableQuantities {
            increment
            maxQty
            minQty
            sellableUom
          }
        }
      }
    `;
    const variables = {
      productId,
    };

    try {
      const resp = await graphqlClient(query, variables);
      return resp.data.product;
    } catch (err) {
      console.log('[ERROR] ----------- getProduct--');
      console.log(err.message);
      throw err;
    }
  }
}

export function getSmartSwaps(productId: string) {
  return async function (): Promise<SmartSwapProduct[]> {
    const query = `
      query GetSmartSwaps($productId: String!) {
        smartSwaps(productId: $productId) {
          product {
            productId
            name
            brand
            barcode
            description
            packageSize
            storeId
            imageUrls
            categories {
              level0
              level1
            }
            price {
              amountAsCents
              saveAmountAsCents
              wasAmountAsCents
              tags
              comparativePricing {
                unitOfMeasure
                quantity
                priceAsCents
              }
            }
            sellableQuantities {
              increment
              maxQty
              minQty
              sellableUom
            }
          }
          productSimilarityScore
          ingredientsSimilarityScore
          allergensSimilarityScore
          overallSimilarityScore
        }
      }
    `;
    const variables = {
      productId,
    };

    try {
      const resp = await graphqlClient(query, variables);
      return resp.data.smartSwaps;
    } catch (err) {
      console.log('[ERROR] ----------- getSmartSwaps--');
      console.log(err.message);
      throw err;
    }
  }
}

export function getCategory(categoryId: string) {
  return async function() {
    const query = `
      query GetCategory($categoryId: String!) {
        category(id: $categoryId) {
          uid
          id
          name
          level
          imageUrl
          children {
            uid
            id
            name
            level
            imageUrl
          }
        }
      }
    `;
    const variables = {
      categoryId,
    };
    try {
      const resp = await graphqlClient(query, variables);
      return resp.data.category;
    } catch (err) {
      console.log('[ERROR] ----------- getCategory --');
      console.log(err.message);
      throw err;
    }
  }
}

export async function getCategories(): Promise<Categories> {
  const query = `
    query GetCategories {
      categories {
        categories {
          uid
          id
          name
          level
          imageUrl
          children {
            uid
            id
            name
            level
            imageUrl
            children {
              id
              name
              level
              imageUrl
            }
          }
        } 
      }
    }
  `;
  try {
    const resp = await graphqlClient(query, {});
    return resp.data.categories;
  } catch (err) {
    console.log('[ERROR] ----------- getCategories --');
    console.log(err.message);
    throw err;
  }
}

export function getCategoryMappings(shoppyCategoryId: string) {
  return async function(): Promise<CategoryMapping[]> {
    const query = `
    query GetCategoryMappings($shoppyCategoryId: String!) {
      categoryMappings(shoppyCategoryId: $shoppyCategoryId) {
        uid
        storeId
        shoppyCategoryId
        storeCategoryId 
      } 
    } 
  `;

    try {
      const resp = await graphqlClient(query, { shoppyCategoryId });
      return resp.data.categoryMappings;
    } catch (err) {
      console.log('[ERROR] ----------- getCategoryMappings --');
      console.log(err.message);
      throw err;
    }

  }
}

export async function addCategoryMapping(categoryMappingInput: CategoryMappingInput): Promise<CategoryMapping> {
  const query = `
    mutation AddCategoryMapping($categoryMappingInput: CategoryMappingInput!) {
      addCategoryMapping(categoryMappingInput: $categoryMappingInput) {
        uid
        shoppyCategoryId
        storeId
        storeCategoryId
      }
    }
  `;
  try {
    const resp = await graphqlClient(query, { categoryMappingInput });
    return resp.data.addCategoryMapping;
  } catch (err) {
    console.log('[ERROR] ----------- addCategoryMapping --');
    console.log(err.message);
    throw err;
  }
}

export async function deleteCategoryMapping(uid: number): Promise<boolean> {
  const query = `
    mutation DeleteCategoryMapping($uid: Int!) {
      deleteCategoryMapping(uid: $uid)
    }
  `;
  try {
    const resp = await graphqlClient(query, { uid });
    return resp.data.deleteCategoryMapping;
  } catch (err) {
    console.log('[ERROR] ----------- deleteCategoryMapping --');
    console.log(err.message);
    throw err;
  }
}

export async function getQuerySuggestions(): Promise<QuerySuggestions> {
  const query = `
    query GetQuerySuggestions {
      querySuggestions {
        suggestions {
          uid
          suggestion
        }
      }
    }
  `;
  try {
    const resp = await graphqlClient(query, {});
    return resp.data.querySuggestions;
  } catch (err) {
    console.log('[ERROR] ----------- getQuerySuggestions --');
    console.log(err.message);
    throw err;
  }
}

export async function addQuerySuggestion(suggestion: string) {
  const query = `
    mutation AddQuerySuggestion($suggestion: String!) {
      addQuerySuggestion(suggestion: $suggestion) {
        uid
        suggestion
      }
    }
  `;
  try {
    const resp = await graphqlClient(query, { suggestion });
    return resp.data.querySuggestions;
  } catch (err) {
    console.log('[ERROR] ----------- addQuerySuggestion --');
    console.log(err.message);
    throw err;
  }
}

export async function deleteQuerySuggestion(uid: number) {
  const query = `
    mutation DeleteQuerySuggestion($uid: Int!) {
      deleteQuerySuggestion(uid: $uid)
    }
  `;
  try {
    const resp = await graphqlClient(query, { uid });
    return resp.data.querySuggestions;
  } catch (err) {
    console.log('[ERROR] ----------- deleteQuerySuggestion --');
    console.log(err.message);
    throw err;
  }
}

export async function updateQuerySuggestion(updateQuerySuggestionInput: QuerySuggestionInput): Promise<QuerySuggestion> {
  const query = `
    mutation UpdateQuerySuggestion($updateQuerySuggestionInput: UpdateQuerySuggestionInput!) {
      updateQuerySuggestion(updateQuerySuggestionInput: $updateQuerySuggestionInput) {
        uid
        suggestion
      }
    }
  `;
  try {
    const resp = await graphqlClient(query, { updateQuerySuggestionInput });
    return resp.data.querySuggestions;
  } catch (err) {
    console.log('[ERROR] ----------- updateQuerySuggestion --');
    console.log(err.message);
    throw err;
  }
}

export async function syncQuerySuggestions() {
  const query = `
    mutation SyncQuerySuggestions {
      syncQuerySuggestions
    }
  `;
  try {
    const resp = await graphqlClient(query, {});
    return resp.data.querySuggestions;
  } catch (err) {
    console.log('[ERROR] ----------- syncQuerySuggestions --');
    console.log(err.message);
    throw err;
  }
}

export async function syncProducts() {
  const mutation = `
    mutation SyncProducts {
      syncProducts 
    } 
  `;
  try {
    const resp = await graphqlClient(mutation, {});
    return resp.data.syncProducts;
  } catch (err) {
    console.log('[ERROR] ----------- syncProducts --');
    console.log(err.message);
    throw err;
  }
}

export async function syncCategories() {
  const mutation = `
    mutation SyncCategories {
      syncCategories
    }
  `;

  try {
    const resp = await graphqlClient(mutation, {});
    return resp.data.syncCategories;
  } catch (err) {
    console.log('[ERROR] ----------- syncCategories --');
    console.log(err.message);
    throw err;
  }
}
